import React, {Fragment, ReactElement, useCallback, useEffect, useMemo, useRef, useState} from "react"
import {Button, Card, Col, DatePicker, Descriptions, Modal, Progress, Row, Table, Tabs, Tag, Tooltip} from "antd"
import {Helmet} from "react-helmet-async"
import {useDispatch, useSelector} from "react-redux"
import {NavLink, useHistory, useLocation, useParams} from "react-router-dom"
import {StateType} from "../../store/store"
import {BackButton} from "../../components/BackButton/BackButton"
import {Loading} from "../../components/Loading/Loading"
import moment from "moment/moment"
import {
    armingPSLeakDevice,
    deletePSLeakDevice,
    getPSLeakDevice, rebootDevice,
    removeTokenPSLeakDevice,
    setPSLeakDevice,
    setPSLeakDeviceSideFilter,
    setWSPSLeakDevice, solveMemoryErrorPSLeakDevice,
    updateDeviceFirmware,
} from "../../store/psLeakDeviceReducer"
import {center} from "../../helps/tableHelper"
import {getPSLeakDeviceLogs, setPSLeakDeviceLogs, setWSPSLeakDeviceLog} from "../../store/psLeakDeviceLogReducer"
import {isSolvedOptionsSelect, limitOptionsSelect, securityChangeModeOptions} from "../../helps/optionsSelect"
import {filterToString} from "../../helps/filterToString"
import {filterFromString} from "../../helps/filterFromString"
import deepEqual from "deep-equal"
import {FormSelect} from "../../components/Form/FormSelect"
import {deletePSLeakDeviceLocalLogs, getPSLeakDeviceLocalLogs, setPSLeakDeviceLocalLogs, setWSPSLeakDeviceLocalLog} from "../../store/psLeakDeviceLocalLogReducer"
import {deletePSLeakDeviceIndications, getPSLeakDeviceIndications, setPSLeakDeviceIndications, setWSPSLeakDeviceIndication} from "../../store/psLeakDeviceIndicationReducer"
import {getClassNameLocalLogRowForTable} from "../../helps/getClassNameLocalLogRowForTable"
import {attributeVersionApi} from "../../api/attributeVersionApi"
import {GroupVersionAttributeType, VersionAttributeType} from "../AttributesVersion/JsonAttributes"
import {armingObject} from "../../store/objectReducer"
import {getClassNameLocalLogLinkText} from "../../helps/getClassNameLocalLogLinkText"
import {BsExclamationCircle, BsExclamationLg, BsShieldCheck, BsShieldX} from "react-icons/bs"
import {catchError} from "../../store/sliceHelper"
import {FormInput} from "../../components/Form/FormInput"
import {AttributesVersionResponse, SecurityLogResponse} from "../../models/response"
import {OptionType, UserTypeEnum} from "../../types/types"
import {useGetUsers} from "../../hooks/useFetchHooks"
import {getSecurityLogs, setSecurityLogs} from "../../store/securityLogReducer"
import {getTimeZoneDiffString} from "../../helps/timezoneDiff"
import {psLeakDeviceApi} from "../../api/psLeakDeviceApi"
import locale from "antd/es/date-picker/locale/ru_RU"
import dayjs from "dayjs"

const columnsSystemLogs = [
    {
        title: "Дата",
        dataIndex: "date",
        key: "date",
        align: center,
    },
    {
        title: "Тип",
        dataIndex: "nameLogType",
        key: "nameLogType",
        align: center,
    },
    {
        title: "Описание",
        dataIndex: "description",
        key: "description",
    },
]

const columnsLocalLogs = [
    {
        title: "Локальный ID",
        dataIndex: "idLocal",
        key: "idLocal",
        align: center,
    },
    {
        title: "Дата",
        dataIndex: "date",
        key: "date",
        align: center,
    },
    {
        title: "Тип",
        dataIndex: "nameLogType",
        key: "nameLogType",
        align: center,
    },
    {
        title: "Описание",
        dataIndex: "description",
        key: "description",
    },
    {
        title: "Статус",
        dataIndex: "solved",
        key: "solved",
        render: (solved: boolean | null) =>
        {
            if (typeof solved === "boolean") return (
                <Tag color={solved ? "green" : "volcano"} style={{marginRight: 0}}>{solved ? "Решено" : "Не решено"}</Tag>
            )
            return <Tag color="default" style={{marginRight: 0}}>Системное</Tag>
        },
        align: center,
    },
]

const columnsIndications = [
    {
        title: "Дата",
        dataIndex: "date",
        key: "date",
        align: center,
    },
    {
        title: "Название",
        dataIndex: "name",
        key: "name",
        align: center,
    },
    {
        title: "Значение",
        dataIndex: "value",
        key: "value",
    },
    {
        title: "Ед. измерения",
        dataIndex: "units",
        key: "units",
    },
]

const columnsSecurityLogs = [
    {
        title: "Дата",
        dataIndex: "date",
        key: "date",
        align: center,
    },
    {
        title: "Сообщение",
        dataIndex: "action",
        key: "action",
        align: center,
    },
    {
        title: "Режим",
        dataIndex: "changeModeUser",
        key: "changeModeUser",
        align: center,
        render: (changeModeUser: { changeMode: SecurityLogResponse["changeMode"], user?: { id: number, name: string, email: string } }) =>
        {
            const {changeMode, user} = changeModeUser
            switch (changeMode)
            {
                case "MANUAL":
                    return `Ручной${user ? ` (${user.name} - ${user.email})` : ""}`
                case "SCHEDULE":
                    return "Расписание"
                case "DEVICE":
                    return "Устройство"
                default:
                    return changeMode
            }
        },
    },
]

export const PSLeakDevice = () =>
{
    const dispatch = useDispatch()
    const history = useHistory()
    const location = useLocation()
    const id = parseInt(useParams<{ id: string }>()?.id, 10)
    const profile = useSelector((state: StateType) => state.appReducer.profile)
    const status = useSelector((state: StateType) => state.appReducer.status)
    const psLeakDevice = useSelector((state: StateType) => state.psLeakDeviceReducer.psLeakDevice)
    const filter = useSelector((state: StateType) => state.psLeakDeviceReducer.sideFilter)
    const psLeakDeviceLogs = useSelector((state: StateType) => state.psLeakDeviceLogReducer.psLeakDeviceLogs)
    const psLeakDeviceLocalLogs = useSelector((state: StateType) => state.psLeakDeviceLocalLogReducer.psLeakDeviceLocalLogs)
    const psLeakDeviceIndications = useSelector((state: StateType) => state.psLeakDeviceIndicationReducer.psLeakDeviceIndications)
    const securityLogs = useSelector((state: StateType) => state.securityLogReducer.securityLogs)
    const ws = useSelector((state: StateType) => state.psLeakDeviceReducer.ws)
    const wsDeviceLog = useSelector((state: StateType) => state.psLeakDeviceLogReducer.ws)
    const wsDeviceLocalLog = useSelector((state: StateType) => state.psLeakDeviceLocalLogReducer.ws)
    const wsDeviceIndication = useSelector((state: StateType) => state.psLeakDeviceIndicationReducer.ws)
    const wsSecurityLogs = useSelector((state: StateType) => state.securityLogReducer.ws)
    const [showModal, setShowModal] = useState(false)
    const [showTokenModal, setShowTokenModal] = useState(false)
    const [showUpdateFirmwareModal, setShowUpdateFirmwareModal] = useState(false)
    const [showRebootDeviceModal, setShowRebootDeviceModal] = useState(false)
    const [showModalDeleteLocalLogs, setShowModalDeleteLocalLogs] = useState(false)
    const [showModalDeleteIndications, setShowModalDeleteIndications] = useState(false)
    const [showSolveMemoryErrorModal, setShowSolveMemoryErrorModal] = useState(false)
    const [showDeviceInfoModal, setShowDeviceInfoModal] = useState(false)
    const [deviceInfoLoading, setDeviceInfoLoading] = useState(false)
    const [deviceInfoError, setDeviceInfoError] = useState<string | null>(null)
    const [deviceInfo, setDeviceInfo] = useState<string | null>(null)
    const [serverAttributesVersion, setServerAttributesVersion] = useState<AttributesVersionResponse | null>(null)
    const [tableSize, setTableSize] = useState(0)
    const ref = useRef<HTMLDivElement>(null)
    const [loading, setLoading] = useState(false)
    const [password, setPassword] = useState("")
    const controller = useMemo(() => new AbortController(), [id])

    const usersQuery = useMemo(() => ({
        idCompany: psLeakDevice?.idCompany,
    }), [psLeakDevice])
    const users = useGetUsers(usersQuery)

    useEffect(() =>
    {
        if (status !== "loading") setTableSize(0)
    }, [status])

    useEffect(() =>
    {
        const filterString = filterFromString(location.search)
        if (filterString && !deepEqual(filterString, filter))
        {
            dispatch(setPSLeakDeviceSideFilter({...filterString}))
        }
        else if (location.search !== filterToString(filter))
        {
            history.replace({search: filterToString(filter)})
        }
    }, [dispatch, filter, history, location])

    useEffect(() =>
    {
        if (id) dispatch(getPSLeakDevice({id, controller}))

        return () =>
        {
            controller.abort()
            dispatch(setPSLeakDevice(null))
        }
    }, [dispatch, id, controller])

    const getPSLeakDeviceLogsFunc = useCallback((withoutLoadingIndicator = false) =>
    {
        if (id && location?.search)
        {
            const filter = filterFromString(location.search)
            if (filter?.tabKey === "1")
            {
                if (filter.limitDeviceLogs) filter.limit = filter.limitDeviceLogs
                if (filter.offsetDeviceLogs) filter.offset = filter.offsetDeviceLogs
                if (filter.dateFromDeviceLogs) filter.dateFrom = filter.dateFromDeviceLogs
                if (filter.dateToDeviceLogs) filter.dateTo = filter.dateToDeviceLogs
                if (filter.searchDeviceLogs) filter.search = filter.searchDeviceLogs
                dispatch(getPSLeakDeviceLogs({idDevice: id, ...filter, controller, withoutLoadingIndicator}))
            }
        }
    }, [dispatch, id, location, controller])

    const getPSLeakDeviceLocalLogsFunc = useCallback((withoutLoadingIndicator = false) =>
    {
        if (id && location?.search)
        {
            const filter = filterFromString(location.search)
            if (filter?.tabKey === "2")
            {
                if (filter.limitDeviceLocalLogs) filter.limit = filter.limitDeviceLocalLogs
                if (filter.offsetDeviceLocalLogs) filter.offset = filter.offsetDeviceLocalLogs
                if (filter.statusDeviceLocalLogs) filter.search = filter.statusDeviceLocalLogs
                if (filter.dateFromDeviceLocalLogs) filter.dateFrom = filter.dateFromDeviceLocalLogs
                if (filter.dateToDeviceLocalLogs) filter.dateTo = filter.dateToDeviceLocalLogs
                if (filter.searchDeviceLocalLogs) filter.search = filter.searchDeviceLocalLogs
                dispatch(getPSLeakDeviceLocalLogs({idDevice: id, ...filter, controller, withoutLoadingIndicator}))
            }
        }
    }, [dispatch, id, location, controller])

    const getPSLeakDeviceIndicationsFunc = useCallback((withoutLoadingIndicator = false) =>
    {
        if (id && location?.search)
        {
            const filter = filterFromString(location.search)
            if (filter?.tabKey === "3")
            {
                if (filter.limitIndications) filter.limit = filter.limitIndications
                if (filter.offsetIndications) filter.offset = filter.offsetIndications
                if (filter.searchIndications) filter.search = filter.searchIndications
                dispatch(getPSLeakDeviceIndications({idDevice: id, ...filter, controller, withoutLoadingIndicator}))
            }
        }
    }, [dispatch, id, location, controller])

    const getSecurityLogsFunc = useCallback((withoutLoadingIndicator = false) =>
    {
        if (id && location?.search)
        {
            const filter = filterFromString(location.search)
            if (filter?.tabKey === "4")
            {
                if (filter.limitSecurityLogs) filter.limit = filter.limitSecurityLogs
                if (filter.offsetSecurityLogs) filter.offset = filter.offsetSecurityLogs
                if (filter.dateFromSecurityLogs) filter.dateFrom = filter.dateFromSecurityLogs
                if (filter.dateToSecurityLogs) filter.dateTo = filter.dateToSecurityLogs
                if (filter.searchSecurityLogs) filter.search = filter.searchSecurityLogs
                dispatch(getSecurityLogs({idDevice: id, ...filter, controller, withoutLoadingIndicator}))
            }
        }
    }, [dispatch, id, location, controller])

    useEffect(() =>
    {
        (async () =>
        {
            if (psLeakDevice?.versionServer && psLeakDevice?.versionLocal)
            {
                try
                {
                    const resServer = await attributeVersionApi.getVersion({id: psLeakDevice.versionServer, controller})
                    if (resServer.data)
                    {
                        setServerAttributesVersion(resServer.data)
                    }
                } catch (e)
                {
                    catchError(e, dispatch)
                }
            }
        })()
    }, [psLeakDevice?.versionServer, psLeakDevice?.versionLocal])

    useEffect(() =>
    {
        getPSLeakDeviceLogsFunc()
        getPSLeakDeviceLocalLogsFunc()
        getPSLeakDeviceIndicationsFunc()
        getSecurityLogsFunc()

        return () =>
        {
            dispatch(setPSLeakDeviceLogs(null))
            dispatch(setPSLeakDeviceLocalLogs(null))
            dispatch(setPSLeakDeviceIndications(null))
            dispatch(setSecurityLogs(null))
        }
    }, [dispatch, getPSLeakDeviceLogsFunc, getPSLeakDeviceLocalLogsFunc, getPSLeakDeviceIndicationsFunc, getSecurityLogsFunc])

    useEffect(() =>
    {
        if (ws && ws.id === id)
        {
            dispatch(getPSLeakDevice({id, controller, withoutLoadingIndicator: true}))
        }
        dispatch(setWSPSLeakDevice(null))
    }, [dispatch, ws, id, controller])

    useEffect(() =>
    {
        if (wsDeviceLog && wsDeviceLog.idParent === id)
        {
            getPSLeakDeviceLogsFunc(true)
        }
        dispatch(setWSPSLeakDeviceLog(null))
    }, [dispatch, id, wsDeviceLog, getPSLeakDeviceLogsFunc])

    useEffect(() =>
    {
        if (wsDeviceLocalLog && wsDeviceLocalLog.idParent === id)
        {
            getPSLeakDeviceLocalLogsFunc(true)
        }
        dispatch(setWSPSLeakDeviceLocalLog(null))
    }, [dispatch, id, wsDeviceLocalLog, getPSLeakDeviceLocalLogsFunc])

    useEffect(() =>
    {
        if (wsDeviceIndication && wsDeviceIndication.idParent === id)
        {
            getPSLeakDeviceIndicationsFunc(true)
        }
        dispatch(setWSPSLeakDeviceIndication(null))
    }, [dispatch, id, wsDeviceIndication, getPSLeakDeviceIndicationsFunc])

    useEffect(() =>
    {
        if (wsSecurityLogs)
        {
            if (psLeakDevice?.idObject && wsSecurityLogs.idParent === psLeakDevice.idObject)
            {
                getSecurityLogsFunc(true)
            }
            dispatch(setSecurityLogs(null))
        }
    }, [dispatch, psLeakDevice?.idObject, wsSecurityLogs, getSecurityLogsFunc])

    const getDeviceInfo = useCallback(async () =>
    {
        if (psLeakDevice?.id)
        {
            try
            {
                setDeviceInfo(null)
                setDeviceInfoError(null)
                setShowDeviceInfoModal(true)
                setDeviceInfoLoading(true)

                const result = await psLeakDeviceApi.getDeviceInfo({id: psLeakDevice.id, controller})

                if (result.status === 0)
                {
                    setDeviceInfo(result.data)
                }
                else
                {
                    setDeviceInfoError(result.message)
                }
            } catch (e: any)
            {
                if (e.message === "canceled") return
                let message = "Что-то пошло не так..."
                if (typeof e.response?.data === "string") message = e.response.data
                else if (e.response?.data?.message) message = e.response.data.message
                else if (e.response?.data?.title) message = e.response.data.title
                else if (e.message) message = e.message
                setDeviceInfoError(message)
            } finally
            {
                setDeviceInfoLoading(false)
            }
        }
    }, [psLeakDevice, controller])

    const deleteHandler = useCallback(async () =>
    {
        if (psLeakDevice?.id)
        {
            const {payload}: any = await dispatch(deletePSLeakDevice(psLeakDevice.id))
            if (payload?.status === 0)
            {
                setShowModal(false)
                if (payload?.message === "Удалено") history.goBack()
                else dispatch(getPSLeakDevice({id, controller}))
            }
        }
    }, [psLeakDevice, history, id, controller])

    const removeTokenHandler = useCallback(async () =>
    {
        if (psLeakDevice)
        {
            try
            {
                setLoading(true)

                const {payload}: any = await dispatch(removeTokenPSLeakDevice({
                    deviceId: psLeakDevice.id,
                    controller,
                }))

                if (payload?.status === 0)
                {
                    setShowTokenModal(false)
                }
            } finally
            {
                setLoading(false)
            }
        }
    }, [psLeakDevice, history, id, controller])

    const armingObjectFunc = useCallback(async () =>
    {
        if (psLeakDevice)
        {
            try
            {
                setLoading(true)

                const {payload}: any = await dispatch(armingObject({
                    id: psLeakDevice.idObject,
                    securityActive: !psLeakDevice.objectSecurityActive,
                    controller,
                }))

                if (payload?.status === 0)
                {
                    dispatch(getPSLeakDevice({id, controller}))
                }
            } finally
            {
                setLoading(false)
            }
        }
    }, [psLeakDevice])

    const armingDeviceFunc = useCallback(async () =>
    {
        if (psLeakDevice)
        {
            try
            {
                setLoading(true)

                const {payload}: any = await dispatch(armingPSLeakDevice({
                    deviceId: psLeakDevice.id,
                    status: !psLeakDevice.onGuard,
                    controller,
                }))
            } finally
            {
                setLoading(false)
            }
        }
    }, [psLeakDevice])

    const updateDeviceFirmwareFunc = useCallback(async () =>
    {
        if (psLeakDevice)
        {
            try
            {
                setLoading(true)

                const {payload}: any = await dispatch(updateDeviceFirmware({
                    deviceId: psLeakDevice.id,
                    controller,
                }))

                if (payload?.status === 0)
                {
                    setShowUpdateFirmwareModal(false)
                }
            } finally
            {
                setLoading(false)
            }
        }
    }, [psLeakDevice])

    const rebootDeviceFunc = useCallback(async () =>
    {
        if (psLeakDevice)
        {
            try
            {
                setLoading(true)

                const {payload}: any = await dispatch(rebootDevice({
                    deviceId: psLeakDevice.id,
                    controller,
                }))

                if (payload?.status === 0)
                {
                    setShowRebootDeviceModal(false)
                }
            } finally
            {
                setLoading(false)
            }
        }
    }, [psLeakDevice])

    const solveMemoryErrorHandler = useCallback(async () =>
    {
        if (psLeakDevice)
        {
            try
            {
                setLoading(true)

                const {payload}: any = await dispatch(solveMemoryErrorPSLeakDevice({
                    id: psLeakDevice.id,
                    controller,
                }))

                if (payload?.status === 0)
                {
                    setShowSolveMemoryErrorModal(false)
                }
            } finally
            {
                setLoading(false)
            }
        }
    }, [psLeakDevice, history, id, controller])

    const items = useMemo(() => [
        {
            key: "1",
            label: `Системные логи`,
        },
        {
            key: "2",
            label: (() =>
            {
                const errors: Array<string> = []

                if (psLeakDevice?.localLogsError) errors.push("Устройство присылает старые логи")
                if (psLeakDevice?.idZeroError) errors.push("Устройство присылает ID 0")

                return <div style={{display: "flex", flexDirection: "row", alignItems: "center", gap: 10}}>Логи устройства {errors.length > 0 ?
                    <Tooltip title={errors.join(" . ")}>
                        <BsExclamationCircle size={20} color={"crimson"}/>
                    </Tooltip> : null}</div>
            })(),
        },
        {
            key: "3",
            label: <div style={{display: "flex", flexDirection: "row", alignItems: "center", gap: 10}}>Показания устройства {psLeakDevice?.indicationsError ?
                <Tooltip title="Устройство присылает старые показания">
                    <BsExclamationCircle size={20} color={"crimson"}/>
                </Tooltip> : null}</div>,
        },
        {
            key: "4",
            label: "Логи охраны",
        },
    ], [psLeakDevice])

    const tableContent = useMemo(() =>
    {
        switch (filter?.tabKey)
        {
            case "1":
            {
                const dataSystemLogs = psLeakDeviceLogs?.data?.map(i =>
                {
                    return {
                        key: i.id,
                        id: i.id,
                        nameLogType: i.nameLogType,
                        description: i.description,
                        date: `${moment(i.date).format("DD.MM.YYYY HH:mm")} ${getTimeZoneDiffString(i.timeZone)}`,
                    }
                })

                return <div className="table-responsive" ref={ref} style={{minHeight: tableSize}}>
                    {
                        psLeakDeviceLogs
                            ? <Table columns={columnsSystemLogs}
                                     dataSource={dataSystemLogs}
                                     pagination={filter?.limitDeviceLogs ? {
                                         position: ["bottomLeft", "topLeft"],
                                         pageSize: filter.limitDeviceLogs,
                                         hideOnSinglePage: true,
                                         total: psLeakDeviceLogs.total,
                                         current: filter?.offsetDeviceLogs / filter?.limitDeviceLogs + 1,
                                         onChange: (page, pageSize) =>
                                         {
                                             setTableSize(ref?.current?.offsetHeight || 0)
                                             const obj = {
                                                 limitDeviceLogs: pageSize,
                                                 offsetDeviceLogs: (page - 1) * pageSize,
                                             }
                                             history.replace({search: filterToString({...filter, ...obj})})
                                         },
                                         showSizeChanger: false,
                                     } : false}
                                     className="ant-border-space"
                                     onRow={({id}) => ({
                                         onClick: () => history.push(`/psLeakDeviceLog/${id}`),
                                         onMouseDown: (e) => e.button === 1 && window.open(`/psLeakDeviceLog/${id}`, "_blank", "noopener,noreferrer"),
                                     })}
                            />
                            : <Loading/>
                    }
                </div>
            }
            case "2":
            {
                const dataLocalLogs = psLeakDeviceLocalLogs?.data?.map(i =>
                {
                    return {
                        key: i.id,
                        id: i.id,
                        idLocal: i.idLocal,
                        idLogType: i.idLogType,
                        nameLogType: i.nameLogType,
                        description: i.description,
                        date: `${moment(i.date).format("DD.MM.YYYY HH:mm")} ${getTimeZoneDiffString(i.timeZone)}`,
                        solved: i.solved,
                    }
                })

                return <div className="table-responsive" ref={ref} style={{minHeight: tableSize}}>
                    {
                        psLeakDeviceLocalLogs
                            ? <Table columns={columnsLocalLogs}
                                     dataSource={dataLocalLogs}
                                     pagination={filter?.limitDeviceLocalLogs ? {
                                         position: ["bottomLeft", "topLeft"],
                                         pageSize: filter.limitDeviceLocalLogs,
                                         hideOnSinglePage: true,
                                         total: psLeakDeviceLocalLogs.total,
                                         current: filter?.offsetDeviceLocalLogs / filter?.limitDeviceLocalLogs + 1,
                                         onChange: (page, pageSize) =>
                                         {
                                             setTableSize(ref?.current?.offsetHeight || 0)
                                             const obj = {
                                                 limitDeviceLocalLogs: pageSize,
                                                 offsetDeviceLocalLogs: (page - 1) * pageSize,
                                             }
                                             history.replace({search: filterToString({...filter, ...obj})})
                                         },
                                         showSizeChanger: false,
                                     } : false}
                                     className="ant-border-space"
                                     onRow={({id}) => ({
                                         onClick: () => history.push(`/psLeakDeviceLocalLog/${id}`),
                                         onMouseDown: (e) => e.button === 1 && window.open(`/psLeakDeviceLocalLog/${id}`, "_blank", "noopener,noreferrer"),
                                     })}
                                     rowClassName={(i) => !i.solved ? getClassNameLocalLogRowForTable(i.idLogType) : ""}
                            />
                            : <Loading/>
                    }
                </div>
            }
            case "3":
            {
                const dataIndications = psLeakDeviceIndications?.data?.map(i =>
                {
                    return {
                        key: i.id,
                        id: i.id,
                        value: i.value,
                        units: i.units,
                        name: i.name,
                        date: `${moment(i.date).format("DD.MM.YYYY HH:mm")} ${getTimeZoneDiffString(i.timeZone)}`,
                    }
                })

                return <div className="table-responsive" ref={ref} style={{minHeight: tableSize}}>
                    {
                        psLeakDeviceIndications
                            ? <Table columns={columnsIndications}
                                     dataSource={dataIndications}
                                     pagination={filter?.limitIndications ? {
                                         position: ["bottomLeft", "topLeft"],
                                         pageSize: filter.limitIndications,
                                         hideOnSinglePage: true,
                                         total: psLeakDeviceIndications.total,
                                         current: filter?.offsetIndications / filter?.limitIndications + 1,
                                         onChange: (page, pageSize) =>
                                         {
                                             setTableSize(ref?.current?.offsetHeight || 0)
                                             const obj = {
                                                 limitIndications: pageSize,
                                                 offsetIndications: (page - 1) * pageSize,
                                             }
                                             history.replace({search: filterToString({...filter, ...obj})})
                                         },
                                         showSizeChanger: false,
                                     } : false}
                                     className="ant-border-space"
                                     onRow={({id}) => ({
                                         onClick: () => history.push(`/psLeakDeviceIndication/${id}`),
                                         onMouseDown: (e) => e.button === 1 && window.open(`/psLeakDeviceIndication/${id}`, "_blank", "noopener,noreferrer"),
                                     })}
                            />
                            : <Loading/>
                    }
                </div>
            }
            case "4":
            {
                const dataSecurityLogs = securityLogs?.data?.map(i =>
                {
                    return {
                        key: i.id,
                        id: i.id,
                        action: i.action,
                        type: i.type,
                        date: `${moment(i.date).format("DD.MM.YYYY HH:mm")} ${getTimeZoneDiffString(i.timeZone)}`,
                        changeModeUser: {changeMode: i.changeMode, user: i.idUser ? {id: i.idUser, name: i.nameUser, email: i.emailUser} : null},
                    }
                })

                return <div className="table-responsive" ref={ref} style={{minHeight: tableSize}}>
                    {
                        securityLogs
                            ? <Table columns={columnsSecurityLogs}
                                     dataSource={dataSecurityLogs}
                                     pagination={filter?.limitSecurityLogs ? {
                                         position: ["bottomLeft", "topLeft"],
                                         pageSize: filter.limitSecurityLogs,
                                         hideOnSinglePage: true,
                                         total: securityLogs.total,
                                         current: filter?.offsetSecurityLogs / filter?.limitSecurityLogs + 1,
                                         onChange: (page, pageSize) =>
                                         {
                                             setTableSize(ref?.current?.offsetHeight || 0)
                                             const obj = {
                                                 limitSecurityLogs: pageSize,
                                                 offsetSecurityLogs: (page - 1) * pageSize,
                                             }
                                             history.replace({search: filterToString({...filter, ...obj})})
                                         },
                                         showSizeChanger: false,
                                     } : false}
                                     className="ant-border-space"
                            />
                            : <Loading/>
                    }
                </div>
            }
        }
    }, [psLeakDeviceLogs, psLeakDeviceLocalLogs, psLeakDeviceIndications, securityLogs, history, filter, tableSize])

    const attributesMap = useMemo(() =>
    {
        if (psLeakDevice?.attributesLocal && psLeakDevice?.attributesServer && serverAttributesVersion)
        {
            const attrDevice: { [key: string]: string | number } = JSON.parse(psLeakDevice.attributesLocal)
            const attrServer: { [key: string]: string | number } = JSON.parse(psLeakDevice.attributesServer)
            const attrVersionParsed: Array<GroupVersionAttributeType> = JSON.parse(serverAttributesVersion.attributes)

            if (attrDevice && attrServer && attrVersionParsed)
            {
                const deprecated = psLeakDevice.attributesServerDeprecated || psLeakDevice.attributesLocalDeprecated

                return (
                    <table border={1} style={{
                        borderColor: "#dedede", tableLayout: "auto", borderLeft: "none", borderTop: "none", borderRight: "none",
                        borderBottom: "none", fontWeight: "normal",
                    }}>
                        <thead>
                        <tr>
                            <th scope="col"></th>
                            <th scope="col">{deprecated ? "Устройство" : "Значение"}</th>
                            {deprecated ? <th scope="col">Сервер</th> : null}
                        </tr>
                        </thead>
                        <tbody>
                        <tr>
                            <td align="center">Дата</td>
                            <td align="center">{moment(psLeakDevice.attributesLocalChangeTime).format("DD.MM.YYYY HH:mm")}</td>
                            {deprecated ? <td align="center">{moment(psLeakDevice.attributesServerChangeTime).format("DD.MM.YYYY HH:mm")}</td> : null}
                        </tr>
                        {fillAttributes(attrDevice, attrServer, attrVersionParsed, deprecated)}
                        {
                            deprecated
                                ? <>
                                    <tr>
                                        <th scope="row" colSpan={3}> &nbsp; </th>
                                    </tr>
                                    <tr>
                                        <td></td>
                                        <td align="center">{psLeakDevice.attributesLocalDeprecated ? <Tag color="gold" style={{marginRight: 0, marginTop: 5}}>Атрибуты устарели</Tag> : null}</td>
                                        <td align="center">{psLeakDevice.attributesServerDeprecated ? <Tag color="gold" style={{marginRight: 0, marginTop: 5}}>Атрибуты устарели</Tag> : null}</td>
                                    </tr>
                                </>
                                : null
                        }
                        </tbody>
                    </table>
                )
            }
        }
    }, [psLeakDevice, serverAttributesVersion])

    const currentStatusMap = useMemo(() =>
    {
        if (psLeakDevice)
        {
            const problems: ReactElement[] = []

            if (psLeakDevice.active && psLeakDevice.offline)
            {
                problems.push(<Tag key="offline" color="volcano" style={{marginRight: 0}}>Не выходит на связь</Tag>)
            }

            if (psLeakDevice.lowMemoryNotification)
            {
                problems.push(<Tag key="lowMemoryNotification" color="gold" style={{marginRight: 0}}>Заканчивается память устройства</Tag>)
            }

            if (psLeakDevice.disableLocalData)
            {
                problems.push(<Tag key="disableLocalData" color="gold" style={{marginRight: 0}}>Не принимаются локальные данные</Tag>)
            }

            if (psLeakDevice.memoryError)
            {
                problems.push(<Tooltip key="memoryError"
                                       title="Решить">
                    <Tag color="gold"
                         style={{marginRight: 0, cursor: "pointer", textDecoration: "underline"}}
                         onClick={() => setShowSolveMemoryErrorModal(true)}>Проблемы с записью в память</Tag>
                </Tooltip>)
            }

            if (psLeakDevice.idZeroError)
            {
                problems.push(<Tag key="idZeroError" color="gold" style={{marginRight: 0}}>Устройство присылает ID 0 . {moment(psLeakDevice.lastDateIdZero).format("DD.MM.YYYY HH:mm")}</Tag>)
            }

            if (psLeakDevice.versionLocalNeedUpdate)
            {
                problems.push(<Tag key="versionLocalNeedUpdate" color="gold" style={{marginRight: 0}}>Требуется обновление версии</Tag>)
            }

            if (psLeakDevice.attributesServerDeprecated || psLeakDevice.attributesLocalDeprecated)
            {
                problems.push(<Tag key="attributesDeprecated" color="gold" style={{marginRight: 0}}>Атрибуты устарели</Tag>)
            }

            if (psLeakDevice.currentStatus?.length > 0)
            {
                psLeakDevice?.currentStatus?.forEach(i => problems.push(
                    <div key={`local_log_${i.id}`}>
                        <NavLink className={getClassNameLocalLogLinkText(i.idLogType)} to={`/psLeakDeviceLocalLog/${i.id}`}>{i.description}</NavLink>
                    </div>,
                ))
            }

            try
            {
                const parsed = JSON.parse(psLeakDevice.attributesLocal)

                if (parsed?.enC === 0)
                    problems.push(<Tag key="enC" color="gold" style={{marginRight: 0}}>Контроль ХВС отключен</Tag>)

                if (parsed?.enH === 0)
                    problems.push(<Tag key="enH" color="gold" style={{marginRight: 0}}>Контроль ГВС отключен</Tag>)
            }
            catch {

            }

            return problems.length > 0 ? problems : <div>Проблем нет</div>
        }
    }, [psLeakDevice])

    const deleteLocalLogs = useCallback(async () =>
    {
        if (psLeakDevice)
        {
            try
            {
                setLoading(true)
                const {payload}: any = await dispatch(deletePSLeakDeviceLocalLogs({
                    idDevice: psLeakDevice.id,
                    password: password,
                    controller,
                }))

                if (payload?.status === 0)
                {
                    dispatch(getPSLeakDeviceLocalLogs({idDevice: id, ...filterFromString(location.search), controller}))
                    setShowModalDeleteLocalLogs(false)
                }
            } finally
            {
                setLoading(false)
            }
        }
    }, [psLeakDevice, password])

    const deleteIndications = useCallback(async () =>
    {
        if (psLeakDevice)
        {
            try
            {
                setLoading(true)
                const {payload}: any = await dispatch(deletePSLeakDeviceIndications({
                    idDevice: psLeakDevice.id,
                    password: password,
                    controller,
                }))

                if (payload?.status === 0)
                {
                    dispatch(getPSLeakDeviceIndications({idDevice: id, ...filterFromString(location.search), controller}))
                    setShowModalDeleteIndications(false)
                }
            } finally
            {
                setLoading(false)
            }
        }
    }, [psLeakDevice, password])

    const userOptions = useMemo(() => users?.data?.map(i => ({label: `${i.name} - ${i.email}`, value: i.id})), [users])

    if (!psLeakDevice) return <Loading/>

    return (
        <div className="description-page">
            <Helmet>
                <title>{psLeakDevice?.name}</title>
            </Helmet>
            <div className="description-header">
                <BackButton onClick={() => history.goBack()}/>
                <div>
                    {psLeakDevice?.ipAddress && psLeakDevice?.port && psLeakDevice?.token ? <Button onClick={() => setShowRebootDeviceModal(true)} type="link">Перезагрузить устройство</Button> : null}
                    {psLeakDevice?.ipAddress && psLeakDevice?.port && psLeakDevice?.token ? <Button onClick={getDeviceInfo} type="link">Информация с устройства</Button> : null}
                    {!psLeakDevice?.deleted ? <Button onClick={() => history.push(`/PSLeakDeviceUpdate/${id}`)} type="link">Обновить атрибуты</Button> : null}
                    {!psLeakDevice?.deleted ? <Button type="link" onClick={() => history.push(`/psLeakDeviceForm`, {psLeakDevice})}>Редактировать</Button> : null}
                    {!psLeakDevice?.deleted ? <Button type="link" danger onClick={() => setShowModal(true)}>Удалить</Button> : null}
                </div>
            </div>

            <Descriptions bordered column={{xs: 1, sm: 1, md: 1, lg: 1, xl: 2, xxl: 2}}>
                <Descriptions.Item label="Название">{psLeakDevice.name}</Descriptions.Item>
                <Descriptions.Item label="ID">{psLeakDevice.id}</Descriptions.Item>
                <Descriptions.Item label="UIN">{psLeakDevice.uin}</Descriptions.Item>
                {psLeakDevice.macAddress ? <Descriptions.Item label="MAC адрес">{psLeakDevice.macAddress}</Descriptions.Item> : null}
                {psLeakDevice.ipAddress ? <Descriptions.Item label="IP адрес">{psLeakDevice.ipAddress}</Descriptions.Item> : null}
                {psLeakDevice.port ? <Descriptions.Item label="Порт">{psLeakDevice.port}</Descriptions.Item> : null}
                <Descriptions.Item label="Оборудование">
                    <div className="description-item-status">
                        {
                            psLeakDevice.onGuard
                                ? <Tooltip title="На охране">
                                    <div style={{display: "flex", alignItems: "center"}}><BsShieldCheck size={20} color={"#2bbb00"}/></div>
                                </Tooltip>
                                : <Tooltip title="Не на охране">
                                    <div style={{display: "flex", alignItems: "center"}}><BsShieldX size={20} color={"#ff4949"}/></div>
                                </Tooltip>
                        }
                        {
                            psLeakDevice.ipAddress && psLeakDevice.port && psLeakDevice.token
                                ? <Button onClick={armingDeviceFunc} type="link" size="small" style={{padding: 0}} loading={loading}>
                                    <span style={{color: psLeakDevice.onGuard ? "#ff4949" : "#2bbb00"}}>{psLeakDevice.onGuard ? "Снять с охраны" : "На охрану"}</span>
                                </Button>
                                : null
                        }
                    </div>
                </Descriptions.Item>
                <Descriptions.Item label="Объект">
                    <div className="description-item-status">
                        <NavLink to={`/object/${psLeakDevice.idObject}`}>{psLeakDevice.nameObject}</NavLink>
                        {
                            psLeakDevice.objectSecurityActive
                                ? <Tooltip title="На охране">
                                    <div style={{display: "flex", alignItems: "center"}}><BsShieldCheck size={20} color={"#2bbb00"}/></div>
                                </Tooltip>
                                : <Tooltip title="Не на охране">
                                    <div style={{display: "flex", alignItems: "center"}}><BsShieldX size={20} color={"#ff4949"}/></div>
                                </Tooltip>
                        }
                        {
                            psLeakDevice.objectActive
                                ? <Button onClick={armingObjectFunc} type="link" size="small" style={{padding: 0}} loading={loading}>
                                    <span style={{color: psLeakDevice.objectSecurityActive ? "#ff4949" : "#2bbb00"}}>{psLeakDevice.objectSecurityActive ? "Снять с охраны" : "На охрану"}</span>
                                </Button>
                                : null
                        }
                    </div>
                </Descriptions.Item>
                <Descriptions.Item label="Группа">
                    <NavLink to={`/objectGroup/${psLeakDevice.idObjectGroup}`}>{psLeakDevice.nameObjectGroup}</NavLink>
                </Descriptions.Item>
                <Descriptions.Item label="Компания">
                    <NavLink to={`/company/${psLeakDevice.idCompany}`}>{psLeakDevice.nameCompany}</NavLink>
                </Descriptions.Item>
                <Descriptions.Item label="Создано">{moment(psLeakDevice.createdAt).format("DD.MM.YYYY HH:mm")}</Descriptions.Item>
                <Descriptions.Item label="Обновлено">{moment(psLeakDevice.updatedAt).format("DD.MM.YYYY HH:mm")}</Descriptions.Item>
                <Descriptions.Item label="Статус">
                    <div className="description-item-status">
                        <Tag color={psLeakDevice.active ? "green" : "volcano"} style={{marginRight: 0}}>{psLeakDevice.active ? "Активно" : "Не активно"}</Tag>
                        {psLeakDevice?.deleted ? <Tag color="volcano" style={{marginRight: 0}}>Удалено</Tag> : null}
                    </div>
                </Descriptions.Item>
                {
                    !psLeakDevice.versionLocalNeedUpdate
                        ? <Descriptions.Item label="Версия">{psLeakDevice.versionServer}</Descriptions.Item>
                        : <>
                            <Descriptions.Item label="Версия сервера">
                                <div className="description-item-status">
                                    {psLeakDevice.versionServer}
                                </div>
                            </Descriptions.Item>
                            <Descriptions.Item label={
                                <span>Версия устройства {psLeakDevice.ipAddress && psLeakDevice.port && psLeakDevice.token ?
                                    <Button onClick={() => setShowUpdateFirmwareModal(true)} type="link" size="small" style={{fontSize: 12}}>
                                        Обновить
                                    </Button> : null}
                                </span>
                            }>
                                <div className="description-item-status">
                                    {psLeakDevice.versionLocal}
                                    <Tag color="gold" style={{marginRight: 0}}>Требуется обновление</Tag>
                                </div>
                            </Descriptions.Item>
                        </>
                }
                <Descriptions.Item label={
                    <span>Токен {psLeakDevice.token ? <Button onClick={() => setShowTokenModal(true)} type="link" size="small" style={{fontSize: 12}}>
                        Сбросить
                    </Button> : null}
                    </span>
                }>
                    {psLeakDevice.token}
                </Descriptions.Item>
                <Descriptions.Item label="Атрибуты" styles={{content: {padding: 0}}}>{attributesMap}</Descriptions.Item>
                <Descriptions.Item label="Последняя проверка">{moment(psLeakDevice.lastCheck).format("DD.MM.YYYY HH:mm")}</Descriptions.Item>
                <Descriptions.Item label="Память">
                    <Tooltip title={`Всего ${psLeakDevice.totalMemory} / Использовано ${psLeakDevice.usedMemory}`}>
                        <Progress size={80}
                                  percent={Math.round(psLeakDevice.usedMemory / psLeakDevice.totalMemory * 100)}
                                  type="circle"
                                  status={psLeakDevice.totalMemory === psLeakDevice.usedMemory ? "exception" : "normal"}
                                  format={(percent) => `${percent}%`}
                        />
                    </Tooltip>
                </Descriptions.Item>
                <Descriptions.Item label="Комментарий">{psLeakDevice.comment}</Descriptions.Item>
                <Descriptions.Item label="Текущий статус">
                    <div style={{display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10}}>
                        {currentStatusMap}
                    </div>
                </Descriptions.Item>
            </Descriptions>

            <div className="tabled-side">
                <Row gutter={[24, 0]}>
                    <Col xs={24}>
                        <Card variant="borderless"
                              className="criclebox tablespace mb-24"
                              title={
                                  <Tabs defaultActiveKey="1"
                                        activeKey={filter?.tabKey}
                                        items={items}
                                        size="large"
                                        onTabClick={(key) =>
                                        {
                                            setTableSize(ref?.current?.offsetHeight || 0)
                                            history.replace({search: filterToString({...filter, tabKey: key})})
                                            if (id && filter)
                                            {
                                                if (filter.tabKey === key && filter.tabKey === "1")
                                                {
                                                    dispatch(getPSLeakDeviceLogs({idDevice: id, ...filterFromString(location.search), controller}))
                                                }
                                                if (filter.tabKey === key && filter.tabKey === "2")
                                                {
                                                    dispatch(getPSLeakDeviceLocalLogs({idDevice: id, ...filterFromString(location.search), controller}))
                                                }
                                                if (filter.tabKey === key && filter.tabKey === "3")
                                                {
                                                    dispatch(getPSLeakDeviceIndications({idDevice: id, ...filterFromString(location.search), controller}))
                                                }
                                                if (filter.tabKey === key && filter.tabKey === "4")
                                                {
                                                    dispatch(getSecurityLogs({idDevice: id, ...filterFromString(location.search), controller}))
                                                }
                                            }
                                        }}
                                  />
                              }
                              extra={
                                  <div className="table-title-extra">
                                      {
                                          filter?.tabKey === "1"
                                              ?
                                              <>
                                                  <FormInput placeholder="Поиск"
                                                             containerStyle={{marginBottom: 0, width: "auto", minWidth: 100}}
                                                             value={filter?.searchDeviceLogs}
                                                             name="search"
                                                             onChange={(e) => history.replace({search: filterToString({...filter, searchDeviceLogs: e.target.value, offsetDeviceLogs: 0})})}
                                                             allowClear
                                                  />

                                                  <DatePicker locale={locale}
                                                              showTime
                                                              placeholder="Дата от"
                                                              format="DD.MM.YYYY HH:mm"
                                                              showSecond={false}
                                                              style={{height: 50, width: 150}}
                                                              value={filter.dateFromDeviceLogs ? dayjs(filter.dateFromDeviceLogs) : null}
                                                              onChange={(value) => history.replace({search: filterToString({...filter, dateFromDeviceLogs: value ? value.format("YYYY-MM-DD HH:mm") : null, offsetDeviceLogs: 0})})}
                                                              disabledDate={(currentDate) => currentDate > dayjs(new Date())}
                                                  />

                                                  <DatePicker locale={locale}
                                                              showTime
                                                              placeholder="Дата по"
                                                              format="DD.MM.YYYY HH:mm"
                                                              showSecond={false}
                                                              style={{height: 50, width: 150}}
                                                              value={filter.dateToDeviceLogs ? dayjs(filter.dateToDeviceLogs) : null}
                                                              onChange={(value) => history.replace({search: filterToString({...filter, dateToDeviceLogs: value ? value.format("YYYY-MM-DD HH:mm") : null, offsetDeviceLogs: 0})})}
                                                              disabledDate={(currentDate) => filter.dateFromDeviceLogs ? currentDate < dayjs(filter.dateFromDeviceLogs) || currentDate > dayjs(new Date()) : currentDate > dayjs(new Date())}
                                                  />

                                                  <FormSelect value={filter?.limitDeviceLogs}
                                                              containerStyle={{marginBottom: 0, width: "auto"}}
                                                              dropdownStyle={{minWidth: "min-content"}}
                                                              onChange={(value) =>
                                                              {
                                                                  setTableSize(ref?.current?.offsetHeight || 0)
                                                                  history.replace({search: filterToString({...filter, limitDeviceLogs: value, offsetDeviceLogs: 0})})
                                                              }}
                                                              options={limitOptionsSelect}
                                                              placement="bottomRight"
                                                  />
                                              </>
                                              : null
                                      }

                                      {
                                          filter?.tabKey === "2"
                                              ? <>
                                                  <Button type="default"
                                                          onClick={() => setShowModalDeleteLocalLogs(true)}
                                                  >
                                                      Очистить
                                                  </Button>

                                                  <FormInput placeholder="Поиск"
                                                             containerStyle={{marginBottom: 0, width: "auto", minWidth: 100}}
                                                             value={filter?.searchDeviceLocalLogs}
                                                             name="search"
                                                             onChange={(e) => history.replace({search: filterToString({...filter, searchDeviceLocalLogs: e.target.value, offsetDeviceLocalLogs: 0})})}
                                                             allowClear
                                                  />

                                                  <DatePicker locale={locale}
                                                              showTime
                                                              placeholder="Дата от"
                                                              format="DD.MM.YYYY HH:mm"
                                                              showSecond={false}
                                                              style={{height: 50, width: 150}}
                                                              value={filter.dateFromDeviceLocalLogs ? dayjs(filter.dateFromDeviceLocalLogs) : null}
                                                              onChange={(value) => history.replace({search: filterToString({...filter, dateFromDeviceLocalLogs: value ? value.format("YYYY-MM-DD HH:mm") : null, offsetDeviceLocalLogs: 0})})}
                                                              disabledDate={(currentDate) => currentDate > dayjs(new Date())}
                                                  />

                                                  <DatePicker locale={locale}
                                                              showTime
                                                              placeholder="Дата по"
                                                              format="DD.MM.YYYY HH:mm"
                                                              showSecond={false}
                                                              style={{height: 50, width: 150}}
                                                              value={filter.dateToDeviceLocalLogs ? dayjs(filter.dateToDeviceLocalLogs) : null}
                                                              onChange={(value) => history.replace({search: filterToString({...filter, dateToDeviceLocalLogs: value ? value.format("YYYY-MM-DD HH:mm") : null, offsetDeviceLocalLogs: 0})})}
                                                              disabledDate={(currentDate) => filter.dateFromDeviceLocalLogs ? currentDate < dayjs(filter.dateFromDeviceLocalLogs) || currentDate > dayjs(new Date()) : currentDate > dayjs(new Date())}
                                                  />

                                                  <FormSelect value={filter?.statusDeviceLocalLogs}
                                                              containerStyle={{marginBottom: 0, width: "auto"}}
                                                              dropdownStyle={{minWidth: "min-content"}}
                                                              onChange={(value) =>
                                                              {
                                                                  setTableSize(ref?.current?.offsetHeight || 0)
                                                                  history.replace({search: filterToString({...filter, statusDeviceLocalLogs: value, offsetDeviceLocalLogs: 0})})
                                                              }}
                                                              options={isSolvedOptionsSelect}
                                                              placeholder="Статус"
                                                              allowClear
                                                              placement="bottomRight"
                                                  />

                                                  <FormSelect value={filter?.limitDeviceLocalLogs}
                                                              containerStyle={{marginBottom: 0, width: "auto"}}
                                                              dropdownStyle={{minWidth: "min-content"}}
                                                              onChange={(value) =>
                                                              {
                                                                  setTableSize(ref?.current?.offsetHeight || 0)
                                                                  history.replace({search: filterToString({...filter, limitDeviceLocalLogs: value, offsetDeviceLocalLogs: 0})})
                                                              }}
                                                              options={limitOptionsSelect}
                                                              placement="bottomRight"
                                                  />
                                              </>
                                              : null
                                      }

                                      {
                                          filter?.tabKey === "3"
                                              ? <>
                                                  <Button type="default"
                                                          onClick={() => setShowModalDeleteIndications(true)}
                                                  >
                                                      Очистить
                                                  </Button>

                                                  <FormInput placeholder="Поиск"
                                                             containerStyle={{marginBottom: 0, width: "auto", minWidth: 100}}
                                                             value={filter?.searchIndications}
                                                             name="search"
                                                             onChange={(e) => history.replace({search: filterToString({...filter, searchIndications: e.target.value, offsetIndications: 0})})}
                                                             allowClear
                                                  />

                                                  <FormSelect value={filter?.limitIndications}
                                                              containerStyle={{marginBottom: 0, width: "auto"}}
                                                              dropdownStyle={{minWidth: "min-content"}}
                                                              onChange={(value) =>
                                                              {
                                                                  setTableSize(ref?.current?.offsetHeight || 0)
                                                                  history.replace({search: filterToString({...filter, limitIndications: value, offsetIndications: 0})})
                                                              }}
                                                              options={limitOptionsSelect}
                                                              placement="bottomRight"
                                                  />
                                              </>
                                              : null
                                      }

                                      {
                                          filter?.tabKey === "4"
                                              ? <>
                                                  <FormInput placeholder="Поиск"
                                                             containerStyle={{marginBottom: 0, width: "auto", minWidth: 100}}
                                                             value={filter?.searchSecurityLogs}
                                                             name="search"
                                                             onChange={(e) => history.replace({search: filterToString({...filter, searchSecurityLogs: e.target.value, offsetSecurityLogs: 0})})}
                                                             allowClear
                                                  />

                                                  <DatePicker locale={locale}
                                                              showTime
                                                              placeholder="Дата от"
                                                              format="DD.MM.YYYY HH:mm"
                                                              showSecond={false}
                                                              style={{height: 50, width: 150}}
                                                              value={filter.dateFromSecurityLogs ? dayjs(filter.dateFromSecurityLogs) : null}
                                                              onChange={(value) => history.replace({search: filterToString({...filter, dateFromSecurityLogs: value ? value.format("YYYY-MM-DD HH:mm") : null, offsetSecurityLogs: 0})})}
                                                              disabledDate={(currentDate) => currentDate > dayjs(new Date())}
                                                  />

                                                  <DatePicker locale={locale}
                                                              showTime
                                                              placeholder="Дата по"
                                                              format="DD.MM.YYYY HH:mm"
                                                              showSecond={false}
                                                              style={{height: 50, width: 150}}
                                                              value={filter.dateToSecurityLogs ? dayjs(filter.dateToSecurityLogs) : null}
                                                              onChange={(value) => history.replace({search: filterToString({...filter, dateToSecurityLogs: value ? value.format("YYYY-MM-DD HH:mm") : null, offsetSecurityLogs: 0})})}
                                                              disabledDate={(currentDate) => filter.dateFromSecurityLogs ? currentDate < dayjs(filter.dateFromSecurityLogs) || currentDate > dayjs(new Date()) : currentDate > dayjs(new Date())}
                                                  />

                                                  {
                                                      profile?.idUserType === UserTypeEnum.ADMIN_SYSTEM || profile?.idUserType === UserTypeEnum.ADMIN_SYSTEM
                                                          ? <FormSelect value={filter?.idUser}
                                                                        containerStyle={{marginBottom: 0, width: "auto", minWidth: 100}}
                                                                        dropdownStyle={{minWidth: "min-content"}}
                                                                        onChange={(value) =>
                                                                        {
                                                                            setTableSize(ref?.current?.offsetHeight || 0)
                                                                            history.replace({search: filterToString({...filter, idUser: value, offsetSecurityLogs: 0})})
                                                                        }}
                                                                        options={userOptions}
                                                                        placeholder="Пользователь"
                                                                        allowClear
                                                                        placement="bottomRight"
                                                          />
                                                          : null
                                                  }

                                                  <FormSelect value={filter?.changeMode}
                                                              containerStyle={{marginBottom: 0, width: "auto", minWidth: 100}}
                                                              dropdownStyle={{minWidth: "min-content"}}
                                                              onChange={(value) => history.replace({search: filterToString({...filter, changeMode: value, offsetSecurityLogs: 0})})}
                                                              options={securityChangeModeOptions}
                                                              placeholder="Режим"
                                                              allowClear
                                                              placement="bottomRight"
                                                  />

                                                  <FormSelect value={filter?.limitSecurityLogs}
                                                              containerStyle={{marginBottom: 0, width: "auto"}}
                                                              dropdownStyle={{minWidth: "min-content"}}
                                                              onChange={(value) =>
                                                              {
                                                                  setTableSize(ref?.current?.offsetHeight || 0)
                                                                  history.replace({search: filterToString({...filter, limitSecurityLogs: value, offsetSecurityLogs: 0})})
                                                              }}
                                                              options={limitOptionsSelect}
                                                              placement="bottomRight"
                                                  />
                                              </>
                                              : null
                                      }
                                  </div>
                              }
                        >
                            {tableContent}
                        </Card>
                    </Col>
                </Row>
            </div>

            <Modal title={`Вы уверены что хотите удалить оборудование`}
                   centered
                   open={showModal}
                   onOk={deleteHandler}
                   onCancel={() => setShowModal(false)}
                   okText="Да"
                   okButtonProps={{loading: status === "loading"}}
            />

            <Modal title={`Вы уверены что сбросить токен оборудования`}
                   centered
                   open={showTokenModal}
                   onOk={removeTokenHandler}
                   onCancel={() => setShowTokenModal(false)}
                   okText="Да"
                   okButtonProps={{loading}}
            />

            <Modal title={`Вы уверены что хотите обновить прошивку устройства`}
                   centered
                   open={showUpdateFirmwareModal}
                   onOk={updateDeviceFirmwareFunc}
                   onCancel={() => setShowUpdateFirmwareModal(false)}
                   okText="Да"
                   okButtonProps={{loading}}
            />

            <Modal title={`Вы уверены что хотите перезагрузить устройство`}
                   centered
                   open={showRebootDeviceModal}
                   onOk={rebootDeviceFunc}
                   onCancel={() => setShowRebootDeviceModal(false)}
                   okText="Да"
                   okButtonProps={{loading}}
            />

            <Modal title="Вы уверены что хотите удалить логи устройства?"
                   centered
                   open={showModalDeleteLocalLogs}
                   onOk={deleteLocalLogs}
                   onCancel={() =>
                   {
                       setShowModalDeleteLocalLogs(false)
                   }}
                   afterClose={() =>
                   {
                       setPassword("")
                   }}
                   okText="Удалить"
                   okButtonProps={{loading}}
            >
                <div style={{width: 350, margin: "auto"}}>
                    <FormInput placeholder="Необходимо подтверждение паролем"
                               value={password}
                               onChange={(e) => setPassword(e.target.value)}
                               allowClear
                               isPassword
                               isInvalid={!password}
                    />
                </div>
            </Modal>

            <Modal title="Вы уверены что хотите удалить показания устройства?"
                   centered
                   open={showModalDeleteIndications}
                   onOk={deleteIndications}
                   onCancel={() =>
                   {
                       setShowModalDeleteIndications(false)
                   }}
                   afterClose={() =>
                   {
                       setPassword("")
                   }}
                   okText="Удалить"
                   okButtonProps={{loading}}
            >
                <div style={{width: 350, margin: "auto"}}>
                    <FormInput placeholder="Необходимо подтверждение паролем"
                               value={password}
                               onChange={(e) => setPassword(e.target.value)}
                               allowClear
                               isPassword
                               isInvalid={!password}
                    />
                </div>
            </Modal>

            <Modal title={`Вы уверены что хотите решить проблему?`}
                   centered
                   open={showSolveMemoryErrorModal}
                   onOk={solveMemoryErrorHandler}
                   onCancel={() => setShowSolveMemoryErrorModal(false)}
                   okText="Да"
                   okButtonProps={{loading}}
            />

            <Modal title={"Информация с устройства"}
                   centered
                   open={showDeviceInfoModal}
                   onOk={getDeviceInfo}
                   onCancel={() => setShowDeviceInfoModal(false)}
                   okText="Обновить"
                   okButtonProps={{loading: deviceInfoLoading}}>
                {deviceInfoLoading ? <Loading/> : null}
                {deviceInfoError ? <div style={{color: "crimson"}}>{deviceInfoError}</div> : null}
                {deviceInfo}
            </Modal>
        </div>
    )
}

function fillAttributes(attrsDevice: { [key: string]: string | number }, attrsServer: { [key: string]: string | number }, attributesVersion: Array<GroupVersionAttributeType>,
                        deprecated: boolean)
{
    type AttrType = {
        index: number,
        key: string,
        value: string,
        defaultValue: string | number
        deviceValue?: string | number | null
        serverValue?: string | number | null
        options: Array<OptionType>
    }

    const dict: Record<number, {
        groupId: number,
        groupName: string,
        groupIndex: number,
        attrs: Array<AttrType>
    }> = []

    dict[0] = {
        groupId: 0,
        groupName: "Без группы",
        groupIndex: 0,
        attrs: [],
    }

    attributesVersion?.forEach(g =>
    {
        dict[g.id] = {
            groupId: g.id,
            groupName: g.name,
            groupIndex: g.index,
            attrs: [],
        }

        g.attributes.forEach(a =>
        {
            const attr: AttrType = {
                index: a.index,
                key: a.key,
                value: a.value,
                defaultValue: a.defaultValue,
                options: a.options ?? [],
            }

            if (a.key in attrsDevice)
            {
                attr.deviceValue = attrsDevice[a.key]
                attr.serverValue = attrsServer[a.key]
                delete attrsDevice[a.key]
                delete attrsServer[a.key]
            }

            dict[g.id].attrs.push(attr)
        })
    })

    const wtfGroupId = -999

    if (Object.keys(attrsDevice).length > 0 || Object.keys(attrsServer).length > 0)
    {
        dict[wtfGroupId] = {
            groupId: -999,
            groupName: "Неизвестно",
            groupIndex: 999,
            attrs: [],
        }
    }

    for (const key in attrsDevice)
    {
        dict[wtfGroupId].attrs.push({
            index: 999,
            key: key,
            value: key,
            defaultValue: "",
            deviceValue: attrsDevice[key],
            serverValue: attrsServer[key],
            options: [],
        })
        delete attrsDevice[key]
        delete attrsServer[key]
    }

    for (const key in attrsServer)
    {
        dict[wtfGroupId].attrs.push({
            index: 999,
            key: key,
            value: key,
            defaultValue: "",
            serverValue: attrsServer[key],
            options: [],
        })
    }

    return Object
        .values(dict)
        .filter(i => i.attrs?.length > 0)
        .sort((a, b) => a.groupIndex - b.groupIndex)
        .map((i) => <Fragment key={i.groupId}>
            <tr>
                <th scope="row" colSpan={3}>{i.groupName}</th>
            </tr>
            {i.attrs.sort((a, b) => a.index - b.index).map(i =>
            {
                const deviceValue = i.options.find(f => f.value === i.deviceValue)?.label ?? i.deviceValue
                const serverValue = i.options.find(f => f.value === i.serverValue)?.label ?? i.serverValue
                const highlight = deprecated && deviceValue !== serverValue

                return (
                    <tr key={i.key} style={highlight ? {background: "#f38c00"} : {}}>
                        <td align="center">{i.value}</td>
                        <td align="center">{deviceValue}</td>
                        {deprecated ? <td align="center">{serverValue}</td> : null}
                    </tr>
                )
            })}
        </Fragment>)
}